import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Money is the loudest thing on any ADVisor screen. The label above it is a
 * 9.5–10px uppercase eyebrow; the figure itself is heavy, tight-tracked and
 * never wraps.
 *
 * `whitespace-nowrap` is load-bearing, not cosmetic: `USD 350` breaking across
 * two lines inside a portrait card clips the amount, which is the one number
 * on the card nobody can afford to misread.
 */
const TONES = {
  ink: "text-neutral-900",
  accent: "text-steel-600",
  success: "text-success-fg",
  warning: "text-warning-fg",
  onImage: "text-white",
} as const;

const SIZES = {
  sm: "text-figure-sm",
  md: "text-figure-md",
  lg: "text-figure-lg",
  xl: "text-figure-xl",
} as const;

export interface MoneyFigureProps extends React.HTMLAttributes<HTMLDivElement> {
  label?: React.ReactNode;
  amount: React.ReactNode;
  caption?: React.ReactNode;
  size?: keyof typeof SIZES;
  tone?: keyof typeof TONES;
}

export function MoneyFigure({
  label,
  amount,
  caption,
  size = "xl",
  tone = "ink",
  className,
  ...props
}: MoneyFigureProps) {
  return (
    <div className={cn("min-w-0", className)} {...props}>
      {label && (
        <p className={cn("adv-label mb-1.5", tone === "onImage" && "text-on-image-muted")}>
          {label}
        </p>
      )}
      <p className={cn("whitespace-nowrap", SIZES[size], TONES[tone])}>{amount}</p>
      {caption && (
        <p
          className={cn(
            "mt-1.5 text-fine",
            tone === "onImage" ? "text-on-image-muted" : "text-neutral-500",
          )}
        >
          {caption}
        </p>
      )}
    </div>
  );
}
